import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Layers, Trash2, Edit, Loader2, ArrowDown, Clock, Sparkles } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import CreditUsageWidget from "@/components/CreditUsageWidget";
import { Modal, SandboxBanner } from "@/components/outreach/shared";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const EMPTY_STEP = () => ({
    step_number: 1,
    subject: "",
    body_html: "",
    body_text: "",
    delay_days: 3,
    send_if_no_reply: true,
});

function StepEditor({ index, step, onChange, onRemove }) {
    return (
        <div
            data-testid={`sequence-step-${index}`}
            className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] p-4 space-y-3"
        >
            <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-text-secondary">
                    <span className="h-6 w-6 rounded-full bg-[var(--color-panel-ai)] grid place-items-center text-[10px] font-semibold text-text-main">
                        {index + 1}
                    </span>
                    Step {index + 1}
                </span>
                {index > 0 && (
                    <button
                        data-testid={`sequence-step-${index}-remove`}
                        onClick={onRemove}
                        className="text-text-secondary hover:text-[var(--color-alert)]"
                        aria-label="Remove step"
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                )}
            </div>
            <input
                data-testid={`sequence-step-${index}-subject`}
                value={step.subject}
                onChange={(e) => onChange({ ...step, subject: e.target.value })}
                placeholder="Subject"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--color-primary)]/60"
            />
            <textarea
                data-testid={`sequence-step-${index}-body`}
                value={step.body_text}
                onChange={(e) => {
                    const body_text = e.target.value;
                    const body_html = body_text.split(/\n\n+/).map(p => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("");
                    onChange({ ...step, body_text, body_html });
                }}
                placeholder="Body (Hi {{first_name}}, ...)"
                rows={6}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-sm font-mono focus:outline-none focus:border-[var(--color-primary)]/60"
            />
            {index > 0 && (
                <div className="flex items-center gap-3 flex-wrap">
                    <label className="inline-flex items-center gap-2 text-xs text-text-secondary">
                        <Clock className="h-3.5 w-3.5" />
                        Delay
                        <input
                            data-testid={`sequence-step-${index}-delay`}
                            type="number" min={0} max={90}
                            value={step.delay_days}
                            onChange={(e) => onChange({ ...step, delay_days: Number(e.target.value) })}
                            className="w-16 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-2 py-1 text-xs focus:outline-none"
                        />
                        <span>days</span>
                    </label>
                    <label className="inline-flex items-center gap-2 text-xs text-text-secondary cursor-pointer">
                        <input
                            type="checkbox"
                            checked={step.send_if_no_reply}
                            onChange={(e) => onChange({ ...step, send_if_no_reply: e.target.checked })}
                            className="accent-[var(--color-primary)]"
                        />
                        Send only if no reply
                    </label>
                </div>
            )}
        </div>
    );
}

function SequenceEditor({ open, onClose, initial, onSaved, templates }) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [steps, setSteps] = useState([EMPTY_STEP()]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setName(initial?.name || "");
        setDescription(initial?.description || "");
        setSteps(initial?.steps?.length ? initial.steps.map(s => ({ ...s })) : [EMPTY_STEP()]);
    }, [initial, open]);

    const addStep = () => {
        if (steps.length >= 5) return;
        setSteps((cur) => [...cur, { ...EMPTY_STEP(), step_number: cur.length + 1 }]);
    };
    const removeStep = (i) => {
        setSteps((cur) => cur.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, step_number: idx + 1 })));
    };
    const patchStep = (i, next) => setSteps((cur) => cur.map((s, idx) => idx === i ? next : s));

    const loadFromTemplate = (tpl) => {
        setSteps([{
            ...EMPTY_STEP(),
            subject: tpl.subject,
            body_text: tpl.body_text,
            body_html: tpl.body_html,
        }]);
    };

    const save = async () => {
        if (!name.trim()) { toast.error("Name required"); return; }
        const cleanSteps = steps.map((s, i) => ({ ...s, step_number: i + 1 }));
        for (const s of cleanSteps) {
            if (!s.subject.trim() || !s.body_text.trim()) {
                toast.error(`Step ${s.step_number}: subject and body required`);
                return;
            }
        }
        setSaving(true);
        try {
            const payload = { name, description, steps: cleanSteps };
            if (initial?.id) {
                const { sequence } = await api.outreachSequencePatch(initial.id, payload);
                onSaved(sequence, "updated");
            } else {
                const { sequence } = await api.outreachSequenceCreate(payload);
                onSaved(sequence, "created");
            }
            toast.success("Sequence saved");
        } catch (e) {
            toast.error(e.message || "Save failed");
        } finally { setSaving(false); }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={initial?.id ? "Edit sequence" : "New sequence"}
            subtitle="Up to 5 steps · delay & reply-check per step"
            maxWidth="max-w-4xl"
            testid="sequence-editor-modal"
        >
            <div className="space-y-4">
                <input
                    data-testid="sequence-name"
                    value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Sequence name"
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--color-primary)]/60"
                />
                <input
                    data-testid="sequence-desc"
                    value={description} onChange={(e) => setDescription(e.target.value)}
                    placeholder="Short description"
                    className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--color-primary)]/60"
                />

                {templates?.length > 0 && (
                    <div className="rounded-lg border border-dashed border-[var(--color-border)] bg-transparent p-3">
                        <p className="text-[10px] uppercase tracking-widest text-text-secondary/80 mb-1">
                            Prefill step 1 from a template
                        </p>
                        <div className="flex flex-wrap gap-1">
                            {templates.slice(0, 10).map((t) => (
                                <button
                                    key={t.id}
                                    onClick={() => loadFromTemplate(t)}
                                    data-testid={`seq-prefill-${t.id}`}
                                    className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] px-2.5 py-1 text-xs text-text-secondary hover:text-text-main"
                                >
                                    <Sparkles className="h-3 w-3" /> {t.name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {steps.map((s, i) => (
                    <React.Fragment key={i}>
                        <StepEditor
                            index={i} step={s}
                            onChange={(next) => patchStep(i, next)}
                            onRemove={() => removeStep(i)}
                        />
                        {i < steps.length - 1 && (
                            <div className="flex justify-center text-text-secondary text-xs items-center gap-1">
                                <ArrowDown className="h-3.5 w-3.5" />
                                after {steps[i + 1].delay_days || 0} days
                            </div>
                        )}
                    </React.Fragment>
                ))}

                {steps.length < 5 && (
                    <button
                        data-testid="sequence-add-step"
                        onClick={addStep}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-dashed border-[var(--color-border)] py-2 text-xs text-text-secondary hover:text-text-main hover:border-[var(--color-primary)]/60 transition-colors"
                    >
                        <Plus className="h-3.5 w-3.5" /> Add step
                    </button>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose} className="rounded-full border border-[var(--color-border)] px-4 py-2 text-sm text-text-secondary hover:text-text-main">Cancel</button>
                    <button
                        data-testid="sequence-save-btn"
                        onClick={save} disabled={saving}
                        className="inline-flex items-center gap-2 rounded-full gradient-bg-cta px-5 py-2 text-sm font-medium text-white disabled:opacity-60"
                    >
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        Save
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default function OutreachSequences() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [meta, setMeta] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [seqs, tpls, m] = await Promise.all([
                api.outreachSequences(), api.outreachTemplates(), api.outreachMeta(),
            ]);
            setItems(seqs.items || []);
            setTemplates(tpls.items || []);
            setMeta(m);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this sequence?")) return;
        try {
            await api.outreachSequenceDelete(id);
            setItems((cur) => cur.filter((s) => s.id !== id));
            toast.success("Deleted");
        } catch (e) {
            toast.error(e.message || "Delete failed");
        }
    };

    const onSaved = (seq, mode) => {
        setEditorOpen(false); setEditing(null);
        setItems((cur) => mode === "created" ? [seq, ...cur] : cur.map(s => s.id === seq.id ? seq : s));
    };

    if (!user) return null;

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(c => !c)} />
            <div className="flex-1 min-w-0 flex flex-col">
                <Header />
                <main data-testid="sequences-main" className="flex-1 px-4 md:px-6 py-6 max-w-5xl w-full">
                    <SandboxBanner show={meta?.sandbox} />
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
                        <div>
                            <p className="text-[11px] uppercase tracking-widest text-text-secondary">Sequences</p>
                            <h1 className="mt-1 text-2xl md:text-3xl font-semibold tracking-tight">
                                Sequenze{" "}
                                <span className="gradient-text">multi-step</span>
                            </h1>
                        </div>
                        <div className="flex items-center gap-2">
                            <CreditUsageWidget />
                            <button
                                data-testid="sequence-new-btn"
                                onClick={() => { setEditing(null); setEditorOpen(true); }}
                                className="inline-flex items-center gap-2 rounded-full gradient-bg-cta px-5 py-2.5 text-sm font-medium text-white transition-transform duration-150 hover:scale-[0.98]"
                            >
                                <Plus className="h-4 w-4" /> New sequence
                            </button>
                        </div>
                    </div>

                    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
                        {loading ? (
                            <div className="p-6 text-sm text-text-secondary">Loading…</div>
                        ) : items.length === 0 ? (
                            <div data-testid="sequences-empty" className="p-12 text-center">
                                <div className="mx-auto h-14 w-14 rounded-full bg-[var(--color-panel-ai)] grid place-items-center border border-[var(--color-border)]">
                                    <Layers className="h-5 w-5 text-[var(--color-secondary)]" />
                                </div>
                                <h3 className="mt-4 text-base font-semibold">Nessuna sequenza</h3>
                                <p className="mt-2 text-sm text-text-secondary">Crea la prima per iniziare una campagna.</p>
                            </div>
                        ) : (
                            <ul>
                                {items.map((s, i) => (
                                    <motion.li
                                        key={s.id}
                                        initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.2, delay: i * 0.02 }}
                                        data-testid={`sequence-row-${i}`}
                                        className="flex items-center justify-between border-b border-[var(--color-border)] last:border-b-0 px-5 py-3.5 hover:bg-[var(--color-panel-ai)]/50 transition-colors"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium truncate">{s.name}</p>
                                            <p className="text-xs text-text-secondary truncate">
                                                {s.steps?.length || 0} step · {s.description || "no description"}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                data-testid={`sequence-edit-${i}`}
                                                onClick={() => { setEditing(s); setEditorOpen(true); }}
                                                className="rounded-md p-1.5 text-text-secondary hover:text-text-main hover:bg-[var(--color-panel-ai)]"
                                            ><Edit className="h-4 w-4" /></button>
                                            <button
                                                data-testid={`sequence-delete-${i}`}
                                                onClick={() => remove(s.id)}
                                                className="rounded-md p-1.5 text-text-secondary hover:text-[var(--color-alert)] hover:bg-[var(--color-alert)]/10"
                                            ><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                    </motion.li>
                                ))}
                            </ul>
                        )}
                    </div>
                </main>
            </div>

            <SequenceEditor
                open={editorOpen}
                onClose={() => { setEditorOpen(false); setEditing(null); }}
                initial={editing}
                onSaved={onSaved}
                templates={templates}
            />
        </div>
    );
}
